import React from 'react';
import { Loader2 } from 'lucide-react';
import { ChatMarkdown } from '@/components/writer/ChatMarkdown';
import WriterTurnReceipt from '@/components/writer/WriterTurnReceipt';
import WriterMemoryNoteLine from '@/components/writer/WriterMemoryNoteLine';
import { writerTurnStatus } from '@/lib/writer/turnStatus';
import { cn } from '@/lib/utils';
import { memoryNotesFrom, type WriterMessage } from '@/hooks/useWriterThread';


interface AssistantTurnProps {
  message: WriterMessage;
  projectId: string;
  onOpenSource?: (anchorId: string) => void;
}

/**
 * Coherent Assistant turn that unifies prose, work logs, and diffs.
 * 
 * Provides accessibility (aria-live), streaming stability, and 
 * responsive layout for all assistant response artifacts.
 */
export const AssistantTurn: React.FC<AssistantTurnProps> = ({
  message,
  projectId,
  onOpenSource,
}) => {
  const text = message.parts.map((p) => p.text).join('');
  const { prose, artifacts } = parseBlocks(text);
  const memoryNotes = memoryNotesFrom(message.toolActivity);
  const turnStatus = writerTurnStatus({
    streaming: Boolean(message.streaming),
    hasProse: Boolean(prose),
    activities: message.toolActivity,
    aiSlow: message.aiSlow,
  });

  const hasWork = (message.toolActivity && message.toolActivity.length > 0) || 
                  (message.textDiff && message.textDiff.sections.length > 0) ||
                  (message.diff && !message.diff.is_empty) ||
                  (message.unresolvedCites && message.unresolvedCites.length > 0);


  return (
    <div 
      className="flex flex-col items-start w-full group"
      role="log"
      aria-live="polite"
    >
      <div
        className={cn(
          'max-w-[92%] w-full overflow-hidden rounded-2xl rounded-bl-sm bg-muted text-foreground shadow-sm ring-1 ring-border/40 transition-shadow group-hover:shadow-md',
          message.streaming && "animate-in fade-in duration-500"
        )}
      >
        <div className="px-4 py-3 text-sm leading-relaxed">
          {prose ? (
            <ChatMarkdown text={prose} />
          ) : turnStatus.visible ? (
            <span className="inline-flex items-center gap-2 text-muted-foreground" role="status">
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              {turnStatus.label}
            </span>
          ) : (
            <span className="text-muted-foreground italic">(empty response)</span>
          )}
        </div>

        {hasWork && (
          <WriterTurnReceipt
            textDiff={message.textDiff}
            diff={message.diff}
            activities={message.toolActivity}
            streaming={message.streaming}
            summaryHidden={turnStatus.visible}
            unresolvedCites={message.unresolvedCites}
            onOpenSource={onOpenSource}
          />
        )}
      </div>

      {memoryNotes.length > 0 && <WriterMemoryNoteLine notes={memoryNotes} />}


      {/* 3. Artifacts (e.g. LaTeX Previews) */}
      {artifacts.map((art, i) => (
        <ArtifactPreview
          key={`${art.kind}-${i}`}
          kind={art.kind}
          content={art.content}
          applied={Boolean(
            message.toolActivity?.some((activity) => activity.auto && activity.ok)
          )}
        />
      ))}
    </div>
  );
};

interface Artifact {
  kind: string;
  content: string;
}

/** 
 * Splits assistant reply into prose + fenced artifact blocks. 
 */
export function parseBlocks(text: string): { prose: string; artifacts: Artifact[] } {
  const artifacts: Artifact[] = [];
  const fence = /```(tex|latex|bibtex|tikz|table|calc)\b[^\n]*\n([\s\S]*?)```/g;
  const prose = text.replace(fence, (_m, kind, content) => {
    artifacts.push({ kind, content: content.trim() });
    return '';
  }).trim();
  return { prose, artifacts };
}

const ARTIFACT_LABELS: Record<string, string> = {
  tex: 'LaTeX preview',
  latex: 'LaTeX preview',
  bibtex: 'BibTeX preview',
  tikz: 'Figure preview',
  table: 'Table preview',
  calc: 'Calculation',
};

const ArtifactPreview: React.FC<{ kind: string; content: string; applied?: boolean }> = ({
  kind,
  content,
  applied = false,
}) => {
  const label = ARTIFACT_LABELS[kind] ?? kind;
  return (
    <div className="max-w-[92%] w-full rounded-xl border border-border/70 bg-background overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border/60 bg-muted/40">
        <span className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">
          {label}
          <span className="ml-1.5 text-foreground/40 normal-case tracking-normal">({kind})</span>
        </span>
        <span className="text-[10px] font-medium text-muted-foreground">
          {applied ? '✓ Applied' : 'Preview only'}
        </span>
      </div>
      <pre className="text-xs leading-relaxed font-mono px-3 py-3 overflow-x-auto text-foreground/90 bg-muted/5">
        {content}
      </pre>
    </div>
  );
};

export default AssistantTurn;
